import React, { useEffect, useState } from 'react';
import * as api from '../api/api';
import AdminLayout from './AdminLayout';

const ManageSections = () => {
    const [sections, setSections] = useState([]);
    const [newSection, setNewSection] = useState({ title: '', identifier: '', content: '', order: 0, isVisible: true });
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchSections = async () => {
        setLoading(true);
        try {
            const { data } = await api.fetchSections();
            setSections(data);
        } catch (error) {
            console.error("Failed to fetch sections", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSections();
    }, []);

    const toggleVisibility = async (section) => {
        try {
            await api.updateSection(section.identifier, { ...section, isVisible: !section.isVisible });
            fetchSections();
        } catch (error) {
            console.error("Failed to toggle visibility", error);
        }
    };

    const handleEdit = (section) => {
        setNewSection(section);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setNewSection({ title: '', identifier: '', content: '', order: 0, isVisible: true });
        setIsEditing(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.updateSection(newSection.identifier, newSection);
            fetchSections();
            handleCancel();
        } catch (error) {
            console.error("Failed to save section", error);
        }
    };

    const handleDelete = async (identifier) => {
        if (window.confirm('Are you sure you want to permanently delete this section?')) {
            try {
                await api.deleteSection(identifier);
                fetchSections();
            } catch (error) {
                console.error("Failed to delete section", error);
            }
        }
    };

    return (
        <AdminLayout title="Manage Content Sections">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
                {/* Form Section */}
                <div className="lg:col-span-1">
                    <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6 sticky top-6">
                        <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
                            <h3 className="text-lg font-semibold text-white flex items-center">
                                <i className={`fas ${isEditing ? 'fa-edit text-yellow-500' : 'fa-plus-circle text-green-500'} mr-2`}></i>
                                {isEditing ? 'Edit Section' : 'Add New Section'}
                            </h3>
                            {isEditing && (
                                <button onClick={handleCancel} className="text-xs text-gray-400 hover:text-white transition-colors">
                                    <i className="fas fa-times mr-1"></i> Cancel
                                </button>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
                                <input
                                    className="w-full bg-gray-900 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder-gray-600 sm:text-sm"
                                    value={newSection.title}
                                    onChange={e => setNewSection({ ...newSection, title: e.target.value })}
                                    required
                                    placeholder="Section Title"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Identifier (Unique ID)</label>
                                <div className="relative rounded-md shadow-sm">
                                    <input
                                        className={`w-full bg-gray-900 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-mono text-sm placeholder-gray-600 ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        value={newSection.identifier}
                                        onChange={e => setNewSection({ ...newSection, identifier: e.target.value })}
                                        required
                                        readOnly={isEditing}
                                        placeholder="unique-id"
                                    />
                                    {isEditing && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <i className="fas fa-lock text-gray-500 text-xs"></i>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Content (HTML)</label>
                                <textarea
                                    className="w-full bg-gray-900 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-mono text-xs placeholder-gray-600 h-32 resize-y custom-scrollbar"
                                    value={newSection.content}
                                    onChange={e => setNewSection({ ...newSection, content: e.target.value })}
                                    placeholder="<p>Enter HTML content...</p>"
                                ></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Display Order</label>
                                <input
                                    type="number"
                                    className="w-full bg-gray-900 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder-gray-600 sm:text-sm"
                                    value={newSection.order}
                                    onChange={e => setNewSection({ ...newSection, order: parseInt(e.target.value) })}
                                />
                            </div>
                            <button
                                type="submit"
                                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${isEditing
                                    ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'}`}
                            >
                                {isEditing ? 'Update Section' : 'Create Section'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Table Section */}
                <div className="lg:col-span-2">
                    <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden flex flex-col h-full">
                        <div className="px-6 py-4 border-b border-gray-700 bg-gray-800 flex justify-between items-center bg-gray-800/50 backdrop-blur-sm sticky top-0 z-20">
                            <h3 className="text-lg font-medium text-white flex items-center">
                                <i className="fas fa-list-ul mr-2 text-gray-400"></i> Existing Sections
                            </h3>
                            <span className="bg-gray-700 text-gray-300 py-1 px-2.5 rounded-full text-xs font-bold shadow-inner">
                                {sections.length}
                            </span>
                        </div>

                        <div className="flex-1 overflow-auto custom-scrollbar bg-gray-900/50">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-800 sticky top-0 z-10 shadow-md">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider w-1/3 border-b border-gray-700">Title & ID</th>
                                        <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider w-24 border-b border-gray-700">Order</th>
                                        <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider w-32 border-b border-gray-700">Status</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wider w-32 border-b border-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-gray-800 divide-y divide-gray-700">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-400">
                                                <i className="fas fa-circle-notch fa-spin mr-2"></i> Loading sections...
                                            </td>
                                        </tr>
                                    ) : sections.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-12 text-center text-sm text-gray-500 flex flex-col items-center">
                                                <i className="fas fa-layer-group text-3xl mb-2 opacity-30"></i>
                                                <p>No content sections found.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        sections.sort((a, b) => a.order - b.order).map(section => (
                                            <tr key={section._id} className={`hover:bg-gray-700/50 transition-colors duration-150 group ${isEditing && newSection._id === section._id ? 'bg-indigo-900/20 border-l-2 border-indigo-500' : 'border-l-2 border-transparent'}`}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-semibold text-white group-hover:text-indigo-300 transition-colors cursor-default">{section.title}</span>
                                                        <span className="text-xs text-gray-500 font-mono mt-0.5 bg-gray-900 px-1.5 py-0.5 rounded-sm w-fit shadow-inner border border-gray-700">#{section.identifier}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-700 text-xs font-medium text-gray-300 border border-gray-600 shadow-sm">
                                                        {section.order}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <button
                                                        onClick={() => toggleVisibility(section)}
                                                        className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${section.isVisible ? 'bg-green-500' : 'bg-gray-600'}`}
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${section.isVisible ? 'translate-x-5' : 'translate-x-0'}`}
                                                        />
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleEdit(section)}
                                                        className="text-indigo-400 hover:text-indigo-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all border border-transparent hover:border-indigo-500/30"
                                                        title="Edit Section"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(section.identifier)}
                                                        className="text-red-400 hover:text-red-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all border border-transparent hover:border-red-500/30"
                                                        title="Delete Section"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ManageSections;
